import type { z } from 'zod';

import { apiClient } from '@/lib/api-client';
import { ApiError } from '@/lib/errors';

import { reviewQueueResponseSchema, type ReviewQueueRow } from '../types/schemas';

function parseOrThrow<T>(schema: z.ZodType<T>, raw: unknown, context: string): T {
  const result = schema.safeParse(raw);
  if (!result.success) {
    const first = result.error.issues[0];
    const path = first?.path.join('.') ?? '';
    const detail = first
      ? `${path ? `${path}: ` : ''}${first.message}`
      : 'Unexpected response shape';
    if (import.meta.env.DEV) {
      console.error(`[caseReportQueueService] ${context} response failed schema parse:`, {
        issues: result.error.issues,
        raw,
      });
    }
    throw new ApiError({
      status: 0,
      code: 'SHAPE_MISMATCH',
      message: `${context}: ${detail}`,
    });
  }
  return result.data;
}

export const caseReportQueueService = {
  /**
   * GET /case-reports/review-queue — reports awaiting MY review
   * (reviewerId === me, status='submitted'). Sorted updatedAt desc by
   * the BE. Permission `case_report:review` required; non-reviewers
   * get 403 (route guard hides the page from them anyway). Optional
   * `organizationId` / `locationId` come from the global tenant scope.
   */
  async listReviewQueue(
    params: { organizationId?: string; locationId?: string } = {},
  ): Promise<ReviewQueueRow[]> {
    const { data } = await apiClient.get<{ data: unknown }>('/case-reports/review-queue', {
      params,
    });
    return parseOrThrow(
      reviewQueueResponseSchema,
      data.data,
      'caseReportQueueService.listReviewQueue',
    );
  },
};
